import React from "react";

const FIXED_PANEL_RADIUS_STYLE = {
  borderRadius: 16,
} as const;

const GoogleBrandMark: React.FC = () => (
  <svg
    aria-hidden="true"
    className="h-[18px] w-[18px] shrink-0"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.31h6.44a5.5 5.5 0 0 1-2.39 3.61v3.01h3.87c2.26-2.09 3.57-5.16 3.57-8.66Z"
      fill="#4285F4"
    />
    <path
      d="M12 24c3.24 0 5.96-1.08 7.95-2.92l-3.87-3.01c-1.08.72-2.45 1.15-4.08 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.1A12 12 0 0 0 12 24Z"
      fill="#34A853"
    />
    <path
      d="M5.27 14.26A7.18 7.18 0 0 1 4.9 12c0-.79.14-1.56.37-2.26V6.63H1.27A12.01 12.01 0 0 0 0 12c0 1.93.46 3.76 1.27 5.37l4-3.11Z"
      fill="#FBBC05"
    />
    <path
      d="M12 4.77c1.76 0 3.33.61 4.58 1.81l3.44-3.44C17.95 1.19 15.23 0 12 0 7.31 0 3.27 2.69 1.27 6.63l4 3.11c.95-2.85 3.6-4.97 6.73-4.97Z"
      fill="#EA4335"
    />
  </svg>
);

export type AuthGoogleSectionProps = {
  dividerLabel: string;
  buttonLabel: string;
  enableGoogle: boolean;
  isSubmitting?: boolean;
  onGoogleContinue: () => void;
  unavailableMessage?: string;
};

export const AuthGoogleSection: React.FC<AuthGoogleSectionProps> = ({
  dividerLabel,
  buttonLabel,
  enableGoogle,
  isSubmitting = false,
  onGoogleContinue,
  unavailableMessage = "Google sign-in is unavailable in this environment.",
}) => {
  return (
    <>
      <div className="relative py-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-surface-light px-4 font-sans text-[11px] font-semibold tracking-[0.18em] text-text-secondary">
            {dividerLabel}
          </span>
        </div>
      </div>

      {enableGoogle ? (
        <>
          <button
            aria-label={buttonLabel}
            className="group flex min-h-14 w-full items-center justify-center gap-3 rounded-2xl border border-border-strong bg-surface-light px-4 py-3 shadow-[var(--shadow-card)] transition-[background-color,box-shadow,border-color,transform] duration-200 hover:border-primary/35 hover:bg-background-light hover:shadow-[var(--shadow-soft)] active:scale-[0.995] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-light disabled:cursor-not-allowed disabled:opacity-50"
            data-provider="google"
            data-testid="auth-google-button"
            style={FIXED_PANEL_RADIUS_STYLE}
            type="button"
            onClick={onGoogleContinue}
            disabled={isSubmitting}
          >
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white shadow-sm transition-colors group-hover:border-primary/20 group-hover:bg-info-background/40"
              aria-hidden="true"
            >
              <GoogleBrandMark />
            </span>
            <span className="flex min-w-0 flex-col items-start">
              <span className="font-sans text-[15px] font-semibold leading-5 tracking-[0.01em] text-irongrey">
                {buttonLabel}
              </span>
              <span className="font-sans text-[11px] font-medium leading-4 text-text-secondary">
                Continue with your Intellio Google account
              </span>
            </span>
          </button>
        </>
      ) : (
        <p className="text-center font-sans text-xs text-text-tertiary">
          {unavailableMessage}
        </p>
      )}
    </>
  );
};
